/**
 * Organizations Page
 * 
 * Sprint B.4.6 LOT 4 - Feature 1 Frontend (Partie 2/2)
 * Page listing all user organizations
 * 
 * 📌 PO Requirements:
 * - Loading, Empty, Error, Success states
 * - Pagination (prepared)
 * - Search bar (prepared)
 * - Sort (prepared)
 * - Filters (prepared)
 * - Actions menu (⋮)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { 
  Building2, 
  Plus, 
  Search, 
  Filter, 
  MoreVertical,
  Users,
  Briefcase,
  TrendingUp,
  Loader2,
  AlertCircle,
  ArrowUpDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

// Animation variants (fade + slide léger)
const fadeSlide = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0 }
};

const container = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

export default function OrganizationsPage() {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Pagination (prepared for future)
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  /**
   * Fetch organizations
   */
  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/organizations', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Erreur lors du chargement des organisations');
      }

      const data = await response.json();
      setOrganizations(data.organizations || []);
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
      setError(err.message);
      toast.error('Impossible de charger les organisations');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle organization navigation
   */
  const handleOrganizationClick = (org) => {
    navigate(`/app/organizations/${org.slug}`);
  };

  /**
   * Handle organization actions
   */
  const handleEdit = (org, e) => {
    e.stopPropagation();
    navigate(`/app/organizations/${org.slug}/settings`);
  };

  const handleDelete = async (org, e) => {
    e.stopPropagation();
    
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer "${org.name}" ?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/organizations/${org.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la suppression');
      }

      toast.success('Organisation supprimée avec succès');
      fetchOrganizations();
    } catch (err) {
      console.error('Failed to delete organization:', err);
      toast.error('Erreur lors de la suppression de l\'organisation');
    }
  };

  /**
   * Filter organizations by search query
   */
  const filteredOrganizations = organizations.filter(org =>
    org.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    org.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      <Helmet>
        <title>Organizations - NEXORA AI</title>
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0F] p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeSlide}
            className="mb-8"
          >
            <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                  Organizations
                </h1>
                <p className="text-zinc-400">
                  Gérez vos organisations et leurs membres
                </p>
              </div>
              <Button
                onClick={() => navigate('/app/organizations/new')}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-medium"
                data-testid="create-organization-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle organisation
              </Button>
            </div>

            {/* Search & Filters Bar */}
            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  type="text"
                  placeholder="Rechercher une organisation..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-zinc-900/50 border-zinc-800 text-white placeholder:text-zinc-500 focus:border-purple-500"
                  data-testid="search-organizations-input"
                />
              </div>
              
              {/* Sort Button (prepared) */}
              <Button
                variant="outline"
                className="border-zinc-800 hover:bg-zinc-800"
                disabled
                data-testid="sort-organizations-button"
              >
                <ArrowUpDown className="w-4 h-4 mr-2" />
                Trier
              </Button>

              {/* Filter Button (prepared) */}
              <Button
                variant="outline"
                className="border-zinc-800 hover:bg-zinc-800"
                disabled
                data-testid="filter-organizations-button"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filtres
              </Button>
            </div>
          </motion.div>

          {/* Content States */}
          {loading && <LoadingState />}
          {error && <ErrorState error={error} onRetry={fetchOrganizations} />}
          {!loading && !error && filteredOrganizations.length === 0 && searchQuery && (
            <EmptySearchState />
          )}
          {!loading && !error && organizations.length === 0 && !searchQuery && (
            <EmptyState onCreateClick={() => navigate('/app/organizations/new')} />
          )}
          {!loading && !error && filteredOrganizations.length > 0 && (
            <OrganizationsGrid
              organizations={filteredOrganizations}
              onOrganizationClick={handleOrganizationClick}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          )}
        </div>
      </div>
    </>
  );
}

/**
 * Loading State
 * 📌 PO Requirement: Loading state
 */
function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center py-20">
      <Loader2 className="w-12 h-12 text-purple-500 animate-spin mb-4" />
      <p className="text-zinc-400">Chargement des organisations...</p>
    </div>
  );
}

/**
 * Error State
 * 📌 PO Requirement: Error state
 */
function ErrorState({ error, onRetry }) {
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={fadeSlide}
      className="flex flex-col items-center justify-center py-20"
    >
      <div className="rounded-full bg-red-500/10 p-4 mb-4">
        <AlertCircle className="w-8 h-8 text-red-400" />
      </div>
      <h3 className="text-xl font-semibold text-white mb-2">
        Erreur de chargement
      </h3>
      <p className="text-zinc-400 mb-6 text-center max-w-md">
        {error}
      </p>
      <Button
        onClick={onRetry}
        variant="outline"
        className="border-zinc-800 hover:bg-zinc-800"
      >
        Réessayer
      </Button>
    </motion.div>
  );
}

/**
 * Empty State (no organizations)
 * 📌 PO Requirement: Empty state
 */
function EmptyState({ onCreateClick }) {
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={fadeSlide}
      className="flex flex-col items-center justify-center py-20"
    >
      <div className="rounded-full bg-gradient-to-br from-blue-500/10 to-purple-500/10 p-6 mb-6 border border-blue-500/20">
        <Building2 className="w-12 h-12 text-blue-400" />
      </div>
      <h3 className="text-2xl font-semibold text-white mb-2">
        Aucune organisation
      </h3>
      <p className="text-zinc-400 mb-8 text-center max-w-md">
        Créez votre première organisation pour commencer à gérer vos équipes et workspaces
      </p>
      <Button
        onClick={onCreateClick}
        className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-medium"
      >
        <Plus className="w-4 h-4 mr-2" />
        Créer une organisation
      </Button>
    </motion.div>
  );
}

/**
 * Empty Search State
 */
function EmptySearchState() {
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={fadeSlide}
      className="flex flex-col items-center justify-center py-20"
    >
      <Search className="w-12 h-12 text-zinc-600 mb-4" />
      <h3 className="text-xl font-semibold text-white mb-2">
        Aucun résultat
      </h3>
      <p className="text-zinc-400 text-center max-w-md">
        Aucune organisation ne correspond à votre recherche
      </p>
    </motion.div>
  );
}

/**
 * Organizations Grid
 * 📌 PO Requirement: Success state with cards
 */
function OrganizationsGrid({ organizations, onOrganizationClick, onEdit, onDelete }) {
  return (
    <motion.div
      variants={container}
      initial="hidden"
      animate="visible"
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
    >
      {organizations.map((org) => (
        <OrganizationCard
          key={org.id}
          organization={org}
          onClick={() => onOrganizationClick(org)}
          onEdit={(e) => onEdit(org, e)}
          onDelete={(e) => onDelete(org, e)}
        />
      ))}
    </motion.div>
  );
}

/**
 * Organization Card
 */
function OrganizationCard({ organization, onClick, onEdit, onDelete }) {
  const memberCount = organization.seats || 0;
  const workspaceCount = 0; // TODO: Get from API when available

  return (
    <motion.div
      variants={fadeSlide}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="group relative rounded-2xl bg-gradient-to-br from-zinc-900/50 to-zinc-900/30 backdrop-blur-xl border border-zinc-800/50 p-6 hover:border-purple-500/30 transition-all cursor-pointer shadow-lg hover:shadow-purple-500/10"
      data-testid={`organization-card-${organization.slug}`}
    >
      {/* Actions Menu */}
      <div className="absolute top-4 right-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={(e) => e.stopPropagation()}
              data-testid={`organization-actions-${organization.slug}`}
            >
              <MoreVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
            <DropdownMenuItem onClick={onEdit} className="hover:bg-zinc-800">
              Paramètres
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={onDelete}
              className="text-red-400 hover:bg-red-500/10 hover:text-red-300"
            >
              Supprimer
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Organization Icon */}
      <div className="rounded-xl bg-gradient-to-br from-blue-500/10 to-purple-500/10 p-3 w-fit mb-4 border border-blue-500/20">
        <Building2 className="w-6 h-6 text-blue-400" />
      </div>

      {/* Organization Info */}
      <h3 className="text-lg font-semibold text-white mb-2 pr-8">
        {organization.name}
      </h3>
      
      {organization.description && (
        <p className="text-sm text-zinc-400 mb-4 line-clamp-2">
          {organization.description}
        </p>
      )}

      {/* Stats */}
      <div className="flex items-center gap-4 pt-4 border-t border-zinc-800/50">
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <Users className="w-4 h-4" />
          <span>{memberCount} membre{memberCount > 1 ? 's' : ''}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <Briefcase className="w-4 h-4" />
          <span>{workspaceCount} workspace{workspaceCount > 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Plan Badge */}
      <div className="absolute bottom-4 right-4">
        <span className="text-xs font-medium px-2 py-1 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20">
          {organization.subscription_plan.toUpperCase()}
        </span>
      </div>
    </motion.div>
  );
}
